/**
 * StandardFaultTreeView
 *
 * The default hierarchical tree view for fault trees.
 * This is the original visualization behavior extracted into the strategy pattern.
 * It delegates to the existing buildftGraphFromObject function for backward compatibility.
 */

import * as joint from '@joint/plus';
import {FaultTreeViewType} from '../../../@types/fault-tree-view-type';
import {BaseFaultTreeView} from './base-fault-tree-view';
import {FaultTreeBuildOptions, FaultTreeBuildResult, FaultTreeLayoutOptions} from './fault-tree-view-strategy';
import type {FaultTree} from '../../../@types/faultTree';
import type EventSequence from '../../../@types/event-sequence';
// Import the existing graph builder - this maintains backward compatibility
import {allshapes, buildftGraphFromObject} from '../canvas-actions';

/**
 * Standard fault tree view - the default hierarchical tree layout.
 * Uses a top-to-bottom layout with the root at the top.
 */
export class StandardFaultTreeView extends BaseFaultTreeView {
    readonly viewType = FaultTreeViewType.STANDARD;
    readonly displayName = 'Standard';

    /**
     * Get the default layout options for the standard view.
     * Uses a bottom-directed layout (root at top, children below).
     */
    getLayoutOptions(): FaultTreeLayoutOptions {
        return {
            direction: 'B',      // Top to bottom (B = children below parent)
            siblingGap: 50,      // Horizontal spacing between siblings
            parentGap: 150,      // Vertical spacing between parent and children
        };
    }

    /**
     * Build the fault tree graph using the existing buildftGraphFromObject function.
     * This maintains full backward compatibility with the current behavior.
     */
    buildGraph(
        eventSequence: EventSequence,
        faultTree: FaultTree | null,
        options: FaultTreeBuildOptions
    ): FaultTreeBuildResult {
        // Return empty result if no fault tree
        if (!faultTree) {
            return {
                graph: new joint.dia.Graph({}, { cellNamespace: allshapes }),
                rootElement: null,
                nodeMap: new Map()
            };
        }

        // Delegate to existing function for backward compatibility
        const graph = buildftGraphFromObject(
            eventSequence,
            faultTree,
            options.maxDepth,
            options.expandedNodeIds
        );

        // Find the root element (the one with no incoming links from FT nodes)
        const elements = graph.getElements();
        let rootElement: joint.dia.Element | null = null;

        for (const el of elements) {
            const inboundLinks = graph.getConnectedLinks(el, { inbound: true });
            if (inboundLinks.length === 0) {
                rootElement = el;
                break;
            }
        }

        // If no root found by links (shouldn't happen), fall back to first element
        if (!rootElement && elements.length > 0) {
            rootElement = elements[0];
        }

        // Build node lookup map for quick access
        const nodeMap = new Map<string, joint.dia.Element>();
        for (const el of elements) {
            const data = el.get('data');
            if (data?.id) {
                // Map by both numeric ID and prefixed ID for flexibility
                nodeMap.set(String(data.id), el);
                nodeMap.set('ft-' + data.id, el);
            }
        }

        return {
            graph,
            rootElement,
            nodeMap
        };
    }
}
